import { useState, useEffect } from 'react';
import { useAppSelector } from '../store/hooks';
import { competitionApi, Competition, EventCategory } from '../api/competition.api';
import { userApi } from '../api/user.api';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Divider,
  Link as MuiLink,
  Collapse,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
} from '@mui/material';
import {
  Add,
  EmojiEvents,
  CalendarToday,
  LocationOn,
  Delete,
  Edit,
  OpenInNew,
  Info,
  FilterList,
  ExpandLess,
  ViewModule,
  ViewList,
} from '@mui/icons-material';

export const CompetitionsPage = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [eventCategories, setEventCategories] = useState<EventCategory[]>([
    { category: '', startDate: '', endDate: '' },
  ]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tournamentNumber: '',
    ltaLink: '',
    startDate: '',
    endDate: '',
    location: '',
    grade: '',
    playerId: '',
  });
  const [filters, setFilters] = useState({
    playerName: '',
    tournamentNumber: '',
    grade: '',
    location: '',
    startDate: '',
    endDate: '',
  });
  const [availablePlayers, setAvailablePlayers] = useState<Array<{ id: string; firstName: string; lastName: string }>>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'name' | 'startDate' | 'endDate' | 'grade'>('startDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const isStaff = user?.role === 'ADMIN' || user?.role === 'HEAD_COACH' || user?.role === 'COACH';
  const isAdmin = user?.role === 'ADMIN';

  useEffect(() => {
    fetchCompetitions();
  }, []);

  const fetchCompetitions = async () => {
    try {
      setLoading(true);
      const response = await competitionApi.getCompetitions();
      setCompetitions(response.data.data || []);
    } catch (err) {
      console.error('Failed to load competitions:', err);
      setError('Failed to load competitions');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayers = async () => {
    if (!isStaff) return;
    try {
      const response = await userApi.getUsers();
      const players = response.data.data.filter((u: any) => u.role === 'PLAYER');
      setAvailablePlayers(players);
    } catch (err) {
      console.error('Failed to load players:', err);
    }
  };

  const handleOpenDialog = () => {
    setEditingId(null);
    setDialogOpen(true);
    setFormData({
      name: '',
      description: '',
      tournamentNumber: '',
      ltaLink: '',
      startDate: '',
      endDate: '',
      location: '',
      grade: '',
      playerId: '',
    });
    setEventCategories([{ category: '', startDate: '', endDate: '' }]);
    if (isStaff) {
      fetchPlayers();
    }
  };

  const handleEditDialog = (competition: Competition) => {
    setEditingId(competition.id);
    setDialogOpen(true);
    setFormData({
      name: competition.name,
      description: competition.description || '',
      tournamentNumber: competition.tournamentNumber || '',
      ltaLink: competition.ltaLink || '',
      startDate: new Date(competition.startDate).toISOString().split('T')[0],
      endDate: new Date(competition.endDate).toISOString().split('T')[0],
      location: competition.location || '',
      grade: competition.grade || '',
      playerId: '',
    });
    const events = parseEventCategories(competition.eventCategories);
    setEventCategories(events.length > 0 ? events : [{ category: '', startDate: '', endDate: '' }]);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setError('');
  };

  const handleAddEventCategory = () => {
    setEventCategories([...eventCategories, { category: '', startDate: '', endDate: '' }]);
  };

  const handleRemoveEventCategory = (index: number) => {
    setEventCategories(eventCategories.filter((_, i) => i !== index));
  };

  const handleEventCategoryChange = (index: number, field: keyof EventCategory, value: string) => {
    const updated = [...eventCategories];
    updated[index] = { ...updated[index], [field]: value };
    setEventCategories(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Filter out empty event categories
    const validCategories = eventCategories.filter(
      (cat) => cat.category && cat.startDate && cat.endDate
    );

    try {
      if (editingId) {
        await competitionApi.updateCompetition(editingId, {
          ...formData,
          eventCategories: validCategories.length > 0 ? validCategories : undefined,
        });
        setSuccess('Competition updated successfully!');
      } else {
        // Create competition
        const { playerId, ...competitionData } = formData;
        const response = await competitionApi.createCompetition({
          ...competitionData,
          eventCategories: validCategories.length > 0 ? validCategories : undefined,
        });

        // If staff selected a player, add them to the competition
        if (isStaff && playerId) {
          const competitionId = response.data.data.id;
          await competitionApi.addPlayer(competitionId, playerId);
        }

        setSuccess('Competition added successfully!');
      }

      handleCloseDialog();
      fetchCompetitions();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || `Failed to ${editingId ? 'update' : 'create'} competition`);
      } else {
        setError(`Failed to ${editingId ? 'update' : 'create'} competition`);
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this competition?')) return;

    try {
      await competitionApi.deleteCompetition(id);
      setSuccess('Competition deleted successfully');
      fetchCompetitions();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Failed to delete competition');
      } else {
        setError('Failed to delete competition');
      }
    }
  };

  const parseEventCategories = (jsonString?: string): EventCategory[] => {
    if (!jsonString) return [];
    try {
      return JSON.parse(jsonString);
    } catch {
      return [];
    }
  };

  // Get unique player names for autocomplete
  const getPlayerOptions = () => {
    if (!isStaff) return [];

    const playerMap = new Map<string, string>();

    competitions.forEach((competition) => {
      competition.results?.forEach((result) => {
        if (result.player) {
          const fullName = `${result.player.firstName || ''} ${result.player.lastName || ''}`.trim();
          if (fullName && !playerMap.has(result.player.id)) {
            playerMap.set(result.player.id, fullName);
          }
        }
      });
    });

    return Array.from(playerMap.values()).sort();
  };

  const playerOptions = getPlayerOptions();

  const getFilteredCompetitions = () => {
    if (!isStaff) return competitions;

    return competitions.filter((competition) => {
      // Filter by player name - check in results
      if (filters.playerName) {
        const hasMatchingPlayer = competition.results?.some((result) => {
          const fullName = `${result.player?.firstName || ''} ${result.player?.lastName || ''}`.toLowerCase();
          return fullName.includes(filters.playerName.toLowerCase());
        });
        if (!hasMatchingPlayer) return false;
      }

      // Filter by tournament number
      if (filters.tournamentNumber && !competition.tournamentNumber?.toLowerCase().includes(filters.tournamentNumber.toLowerCase())) {
        return false;
      }

      // Filter by grade
      if (filters.grade && competition.grade !== filters.grade) {
        return false;
      }

      // Filter by location
      if (filters.location && !competition.location?.toLowerCase().includes(filters.location.toLowerCase())) {
        return false;
      }

      // Filter by start date
      if (filters.startDate) {
        const competitionStart = new Date(competition.startDate);
        const filterStart = new Date(filters.startDate);
        if (competitionStart < filterStart) return false;
      }

      // Filter by end date
      if (filters.endDate) {
        const competitionEnd = new Date(competition.endDate);
        const filterEnd = new Date(filters.endDate);
        if (competitionEnd > filterEnd) return false;
      }

      return true;
    });
  };

  const getSortedCompetitions = (comps: Competition[]) => {
    return [...comps].sort((a, b) => {
      let compareValue = 0;

      switch (sortBy) {
        case 'name':
          compareValue = a.name.localeCompare(b.name);
          break;
        case 'startDate':
          compareValue = new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
          break;
        case 'endDate':
          compareValue = new Date(a.endDate).getTime() - new Date(b.endDate).getTime();
          break;
        case 'grade':
          compareValue = (a.grade || '').localeCompare(b.grade || '');
          break;
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });
  };

  const filteredCompetitions = getSortedCompetitions(getFilteredCompetitions());

  const handleClearFilters = () => {
    setFilters({
      playerName: '',
      tournamentNumber: '',
      grade: '',
      location: '',
      startDate: '',
      endDate: '',
    });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EmojiEvents sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">{isStaff ? 'Competitions' : 'My Competitions'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModule />
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <ViewList />
            </ToggleButton>
          </ToggleButtonGroup>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              label="Sort By"
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            >
              <MenuItem value="startDate">Start Date</MenuItem>
              <MenuItem value="endDate">End Date</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="grade">Grade</MenuItem>
            </Select>
          </FormControl>

          <ToggleButtonGroup
            value={sortOrder}
            exclusive
            onChange={(_, newOrder) => newOrder && setSortOrder(newOrder)}
            size="small"
          >
            <ToggleButton value="asc" aria-label="ascending">
              ↑
            </ToggleButton>
            <ToggleButton value="desc" aria-label="descending">
              ↓
            </ToggleButton>
          </ToggleButtonGroup>

          {isStaff && (
            <Button
              variant="outlined"
              startIcon={filterOpen ? <ExpandLess /> : <FilterList />}
              onClick={() => setFilterOpen(!filterOpen)}
            >
              {filterOpen ? 'Hide' : 'Filter'}
            </Button>
          )}
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenDialog}>
            Add Competition
          </Button>
        </Box>
      </Box>

      {isStaff && (
        <Collapse in={filterOpen}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Filter Competitions
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  fullWidth
                  freeSolo
                  options={playerOptions}
                  value={filters.playerName}
                  onChange={(_, newValue) => setFilters({ ...filters, playerName: newValue || '' })}
                  onInputChange={(_, newInputValue) => setFilters({ ...filters, playerName: newInputValue })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Player Name"
                      placeholder="Search by player name"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Tournament Number"
                  value={filters.tournamentNumber}
                  onChange={(e) => setFilters({ ...filters, tournamentNumber: e.target.value })}
                  placeholder="e.g., MID-26-0043"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Grade"
                  value={filters.grade}
                  onChange={(e) => setFilters({ ...filters, grade: e.target.value })}
                  placeholder="e.g., 1, 2, 3, 4"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Location"
                  value={filters.location}
                  onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                  placeholder="Search by location"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Start Date (From)"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="End Date (To)"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={handleClearFilters}>Clear Filters</Button>
            </Box>
          </Paper>
        </Collapse>
      )}

      {user?.role === 'PLAYER' && user.profile?.btn && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Info />
            <Typography variant="body2">
              View your LTA player profile:{' '}
              <MuiLink
                href={`https://competitions.lta.org.uk/find/player?q=${encodeURIComponent(
                  user.profile.btn
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'inherit', fontWeight: 600 }}
              >
                LTA Profile <OpenInNew fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
              </MuiLink>
            </Typography>
          </Box>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {isStaff && !loading && competitions.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredCompetitions.length} of {competitions.length} competitions
          </Typography>
        </Box>
      )}

      {loading ? (
        <Typography>Loading...</Typography>
      ) : filteredCompetitions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <EmojiEvents sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {competitions.length === 0 ? 'No competitions yet' : 'No competitions match your filters'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {competitions.length === 0 ? 'Add your first competition to track your progress' : 'Try adjusting your filter criteria'}
          </Typography>
          {competitions.length === 0 && (
            <Button variant="contained" startIcon={<Add />} onClick={handleOpenDialog}>
              Add Competition
            </Button>
          )}
        </Paper>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {filteredCompetitions.map((competition) => {
            const events = parseEventCategories(competition.eventCategories);
            return (
              <Grid item xs={12} md={6} key={competition.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                      <Typography variant="h6" component="div">
                        {competition.name}
                      </Typography>
                      {(competition.createdBy === user?.id || isAdmin) && (
                        <Box>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditDialog(competition)}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(competition.id)}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      )}
                    </Box>

                    {isStaff && competition.results && competition.results.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Player(s):</strong>{' '}
                          {competition.results
                            .map((r) => `${r.player?.firstName || ''} ${r.player?.lastName || ''}`.trim())
                            .filter(Boolean)
                            .join(', ')}
                        </Typography>
                      </Box>
                    )}

                    {competition.tournamentNumber && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Tournament #:</strong> {competition.tournamentNumber}
                      </Typography>
                    )}

                    {competition.grade && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Grade:</strong> {competition.grade}
                      </Typography>
                    )}

                    {competition.description && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {competition.description}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1 }}>
                      <CalendarToday fontSize="small" />
                      <Typography variant="body2">
                        {new Date(competition.startDate).toLocaleDateString()} -{' '}
                        {new Date(competition.endDate).toLocaleDateString()}
                      </Typography>
                    </Box>

                    {competition.location && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <LocationOn fontSize="small" />
                        <Typography variant="body2">{competition.location}</Typography>
                      </Box>
                    )}

                    {events.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 1 }} />
                        <Typography variant="subtitle2" gutterBottom>
                          Event Categories:
                        </Typography>
                        {events.map((event, idx) => (
                          <Box key={idx} sx={{ ml: 2, mb: 1 }}>
                            <Typography variant="caption" display="block">
                              <strong>{event.category}</strong>
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(event.startDate).toLocaleDateString()} -{' '}
                              {new Date(event.endDate).toLocaleDateString()}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {competition.ltaLink && (
                      <Box sx={{ mt: 2 }}>
                        <Button
                          size="small"
                          href={competition.ltaLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<OpenInNew />}
                        >
                          View on LTA
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <List>
          {filteredCompetitions.map((competition) => {
            const events = parseEventCategories(competition.eventCategories);
            return (
              <Paper key={competition.id} sx={{ mb: 2 }}>
                <ListItem
                  sx={{ flexDirection: 'column', alignItems: 'stretch', p: 2 }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1, width: '100%' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6">{competition.name}</Typography>

                      {isStaff && competition.results && competition.results.length > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          <strong>Player(s):</strong>{' '}
                          {competition.results
                            .map((r) => `${r.player?.firstName || ''} ${r.player?.lastName || ''}`.trim())
                            .filter(Boolean)
                            .join(', ')}
                        </Typography>
                      )}

                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarToday fontSize="small" />
                          <Typography variant="body2">
                            {new Date(competition.startDate).toLocaleDateString()} - {new Date(competition.endDate).toLocaleDateString()}
                          </Typography>
                        </Box>

                        {competition.location && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOn fontSize="small" />
                            <Typography variant="body2">{competition.location}</Typography>
                          </Box>
                        )}

                        {competition.tournamentNumber && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Tournament #:</strong> {competition.tournamentNumber}
                          </Typography>
                        )}

                        {competition.grade && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Grade:</strong> {competition.grade}
                          </Typography>
                        )}
                      </Box>

                      {competition.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {competition.description}
                        </Typography>
                      )}

                      {events.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                            Event Categories:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                            {events.map((event, idx) => (
                              <Chip
                                key={idx}
                                label={`${event.category} (${new Date(event.startDate).toLocaleDateString()} - ${new Date(event.endDate).toLocaleDateString()})`}
                                size="small"
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {competition.ltaLink && (
                        <Box sx={{ mt: 1 }}>
                          <Button
                            size="small"
                            href={competition.ltaLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<OpenInNew />}
                          >
                            View on LTA
                          </Button>
                        </Box>
                      )}
                    </Box>

                    {(competition.createdBy === user?.id || isAdmin) && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditDialog(competition)}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(competition.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </ListItem>
              </Paper>
            );
          })}
        </List>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>{editingId ? 'Edit Competition' : 'Add Competition'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Competition Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>

              {isStaff && !editingId && (
                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    options={availablePlayers}
                    getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                    value={availablePlayers.find((p) => p.id === formData.playerId) || null}
                    onChange={(_, newValue) => setFormData({ ...formData, playerId: newValue?.id || '' })}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Player (Optional)"
                        placeholder="Select a player"
                        helperText="Select which player this competition is for"
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Tournament Number (e.g., MID-26-0043)"
                  value={formData.tournamentNumber}
                  onChange={(e) => setFormData({ ...formData, tournamentNumber: e.target.value })}
                  placeholder="MID-26-0043"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="LTA Link"
                  value={formData.ltaLink}
                  onChange={(e) => setFormData({ ...formData, ltaLink: e.target.value })}
                  placeholder="https://competitions.lta.org.uk/..."
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Competition Start Date"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Competition End Date"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Grade"
                  value={formData.grade}
                  onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                  placeholder="e.g., 1, 2, 3, 4"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">Event Categories (Optional)</Typography>
                  <Button size="small" onClick={handleAddEventCategory} startIcon={<Add />}>
                    Add Category
                  </Button>
                </Box>

                {eventCategories.map((event, index) => (
                  <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Category Name"
                          value={event.category}
                          onChange={(e) =>
                            handleEventCategoryChange(index, 'category', e.target.value)
                          }
                          placeholder="e.g., Men's Singles, Women's Doubles"
                        />
                      </Grid>
                      <Grid item xs={12} sm={5}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Start Date"
                          type="date"
                          value={event.startDate}
                          onChange={(e) =>
                            handleEventCategoryChange(index, 'startDate', e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={5}>
                        <TextField
                          fullWidth
                          size="small"
                          label="End Date"
                          type="date"
                          value={event.endDate}
                          onChange={(e) =>
                            handleEventCategoryChange(index, 'endDate', e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveEventCategory(index)}
                          disabled={eventCategories.length === 1}
                        >
                          <Delete />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingId ? 'Update Competition' : 'Add Competition'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};
